/**
 * OperatoreAttivoContext — "operatore attivo" per l'attribuzione desktop.
 * Chi firma resta attivo (persistito in localStorage 'operatore_attivo') finché non esce o
 * scade il turno (TIMEOUT). Un widget flottante sempre accessibile permette firma/cambio/uscita.
 * L'operatore attivo viene letto da api.ts (authHeaders) e inviato come x-user-name / x-user-id /
 * x-eseguito-da su ogni azione → i log lo registrano (firmato_da + eseguito_da). NON è autenticazione
 * (quella resta il login admin): è solo attribuzione "chi ha fatto cosa".
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { UserCheck, LogOut, PenLine } from 'lucide-react';
import FirmaOperatoreModal, { type FirmaConfermata } from '../components/operatore/FirmaOperatoreModal';
import ConfermaTransizioneModal from '../components/operatore/ConfermaTransizioneModal';

export interface OperatoreAttivo {
  id: string | number;
  displayName: string;
  eseguitoDa: string;
  firmatoAt: number;
}

interface Ctx {
  operatore: OperatoreAttivo | null;
  firmato: boolean;
  /** Apre il modale firma. onDone opzionale = callback dopo firma riuscita (per azioni critiche). */
  apriFirma: (subtitle?: string, onDone?: (f: FirmaConfermata) => void) => void;
  /** Conferma leggera per OGNI passaggio di stato (firmatario + eseguito da), poi esegue run(). */
  confermaTransizione: (descrizione: string, run: () => void) => void;
  /** Promise: risolve true (confermato) / false (annullato). Per flussi awaited (turbine). */
  richiediConferma: (descrizione: string) => Promise<boolean>;
  esci: () => void;
}

const OperatoreAttivoCtx = createContext<Ctx>({ operatore: null, firmato: false, apriFirma: () => {}, confermaTransizione: () => {}, richiediConferma: () => Promise.resolve(false), esci: () => {} });
export const useOperatoreAttivo = () => useContext(OperatoreAttivoCtx);

export const OP_KEY = 'operatore_attivo';
const TIMEOUT_MS = 20 * 60 * 1000; // 20 min di INATTIVITÀ (firmatoAt viene rinfrescato sull'attività)

function loadStored(): OperatoreAttivo | null {
  try {
    const raw = localStorage.getItem(OP_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o?.displayName) return null;
    if (Date.now() - (o.firmatoAt || 0) > TIMEOUT_MS) { localStorage.removeItem(OP_KEY); return null; }
    return o;
  } catch { return null; }
}

function persist(o: OperatoreAttivo | null) {
  try { if (o) localStorage.setItem(OP_KEY, JSON.stringify(o)); else localStorage.removeItem(OP_KEY); } catch { /* ignore */ }
}

export function OperatoreAttivoProvider({ children }: { children: React.ReactNode }) {
  const [operatore, setOperatore] = useState<OperatoreAttivo | null>(() => loadStored());
  const [gateOpen, setGateOpen] = useState(false);
  const [subtitle, setSubtitle] = useState<string | undefined>();
  const pendingDone = useRef<((f: FirmaConfermata) => void) | null>(null);
  const pendingCancel = useRef<(() => void) | null>(null);
  const [confirmState, setConfirmState] = useState<{ descrizione: string; resolve: (v: boolean) => void } | null>(null);

  const apriFirma = useCallback((st?: string, onDone?: (f: FirmaConfermata) => void, onCancel?: () => void) => {
    setSubtitle(st); pendingDone.current = onDone || null; pendingCancel.current = onCancel || null; setGateOpen(true);
  }, []);

  const esci = useCallback(() => { setOperatore(null); persist(null); }, []);

  const richiediConferma = useCallback((descrizione: string): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      const open = () => setConfirmState({ descrizione, resolve });
      // Se nessuno ha firmato nel turno → firma completa col PIN; se annulla la firma → false.
      if (!loadStored()) { apriFirma('Firma per procedere', open, () => resolve(false)); } else { open(); }
    });
  }, [apriFirma]);

  const confermaTransizione = useCallback((descrizione: string, run: () => void) => {
    richiediConferma(descrizione).then(ok => { if (ok) run(); });
  }, [richiediConferma]);

  const onConfermaTransizione = (eseguitoDa: string) => {
    const cs = confirmState; const op = loadStored();
    setConfirmState(null);
    if (!cs) return;
    if (op) { const updated: OperatoreAttivo = { ...op, eseguitoDa }; setOperatore(updated); persist(updated); }
    cs.resolve(true);
  };

  const onAnnullaTransizione = () => { const cs = confirmState; setConfirmState(null); if (cs) cs.resolve(false); };

  const onConfirm = (f: FirmaConfermata) => {
    const o: OperatoreAttivo = { id: f.firmatarioId, displayName: f.firmatario, eseguitoDa: f.eseguitoDa, firmatoAt: Date.now() };
    setOperatore(o); persist(o); setGateOpen(false);
    pendingCancel.current = null;
    const cb = pendingDone.current; pendingDone.current = null;
    if (cb) cb(f);
  };

  // Auto-lock per INATTIVITÀ: ogni interazione utente rinfresca firmatoAt (throttle 60s);
  // se resta inattivo > TIMEOUT_MS → esci (firma scaduta). Robusto anche dopo reload (loadStored).
  const lastBump = useRef(0);
  useEffect(() => {
    if (!operatore) return;
    const bump = () => {
      const now = Date.now();
      if (now - lastBump.current < 60000) return; // throttle
      lastBump.current = now;
      try { const o = loadStored(); if (o) { o.firmatoAt = now; localStorage.setItem(OP_KEY, JSON.stringify(o)); } } catch { /* ignore */ }
    };
    window.addEventListener('click', bump);
    window.addEventListener('keydown', bump);
    const t = setInterval(() => { if (!loadStored()) esci(); }, 30000);
    return () => { window.removeEventListener('click', bump); window.removeEventListener('keydown', bump); clearInterval(t); };
  }, [operatore, esci]);

  // Mostra il widget solo se loggati nel gestionale (non sulla pagina di login)
  const [logged, setLogged] = useState<boolean>(() => { try { return !!localStorage.getItem('accessToken'); } catch { return false; } });
  useEffect(() => {
    const check = () => { try { setLogged(!!localStorage.getItem('accessToken')); } catch { /* ignore */ } };
    window.addEventListener('storage', check);
    window.addEventListener('focus', check);
    const iv = setInterval(check, 3000);
    return () => { window.removeEventListener('storage', check); window.removeEventListener('focus', check); clearInterval(iv); };
  }, []);

  // Webapp mobile dedicate: la firma operatore non c'entra e la pill copre le
  // barre azioni touch (segnalazione 2026-07-16 su /mobile-preventivi).
  const { pathname } = useLocation();
  const nascondiWidget = pathname.startsWith('/mobile-preventivi');

  return (
    <OperatoreAttivoCtx.Provider value={{ operatore, firmato: !!operatore, apriFirma, confermaTransizione, richiediConferma, esci }}>
      {children}
      {/* Widget flottante sempre accessibile (bottom-left), solo se loggati e NON dentro un iframe
          embedded (es. modale Modifica Analisi carica AnalisiGuastiPage in iframe → altrimenti il
          widget firma apparirebbe due volte: parent + iframe). */}
      {logged && !nascondiWidget && !window.location.search.includes('embedded=true') && (
      <div className="fixed z-[300] bottom-20 left-4 print:hidden">
        {operatore ? (
          <div className="flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full bg-emerald-600/90 backdrop-blur border border-emerald-400/50 shadow-xl text-white">
            <UserCheck className="w-4 h-4" />
            <span className="text-xs font-semibold">Operatore: {operatore.displayName}</span>
            {operatore.eseguitoDa && operatore.eseguitoDa !== operatore.displayName && (
              <span className="text-[10px] opacity-80">(esec: {operatore.eseguitoDa})</span>
            )}
            <button onClick={() => apriFirma()} title="Cambia operatore / esecutore"
              className="ml-1 p-1 rounded-full hover:bg-white/20"><PenLine className="w-3.5 h-3.5" /></button>
            <button onClick={esci} title="Esci (togli firma)"
              className="p-1 rounded-full hover:bg-white/20"><LogOut className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <button onClick={() => apriFirma()}
            className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/70 hover:bg-amber-500 backdrop-blur border border-amber-300/40 shadow-md text-white text-[11px] font-medium">
            <PenLine className="w-3.5 h-3.5" /> Firma operatore
          </button>
        )}
      </div>
      )}
      <FirmaOperatoreModal
        isOpen={gateOpen}
        onClose={() => { setGateOpen(false); pendingDone.current = null; const c = pendingCancel.current; pendingCancel.current = null; if (c) c(); }}
        onConfirm={onConfirm}
        subtitle={subtitle}
      />
      {confirmState && operatore && (
        <ConfermaTransizioneModal
          isOpen={true}
          descrizione={confirmState.descrizione}
          firmatario={operatore.displayName}
          firmatarioId={operatore.id}
          eseguitoDaAttuale={operatore.eseguitoDa}
          onConferma={onConfermaTransizione}
          onAnnulla={onAnnullaTransizione}
          onCambiaFirmatario={() => { const cs = confirmState; setConfirmState(null); apriFirma('Cambia operatore', () => setConfirmState(cs), () => setConfirmState(cs)); }}
        />
      )}
    </OperatoreAttivoCtx.Provider>
  );
}
